import moment from 'moment'

/**
 * Forma mínima necessária para escolher/ordenar ciclos. Tipada de forma
 * estrutural para servir tanto ao `BillingsParams` de `models/Billings`
 * quanto ao de `models/Recurrences`.
 */
export type CycleBilling = {
  uuid?: string
  dueDate?: string
  status?: string
  amountBilled?: string
  amountPaid?: string
  // A API manda inteiro, mas o model de Billings tipa como string — aceita os
  // dois e a comparação normaliza
  recurrenceCycle?: number | string | null
}

const toTimestamp = (date?: string) => moment(date, 'YYYY-MM-DD').valueOf()

/** Normaliza o ciclo, que pode chegar como número ou string da API */
const toCycleNumber = (cycle?: number | string | null) => {
  if (cycle === null || cycle === undefined || cycle === '') return undefined
  const parsed = Number(cycle)
  return Number.isFinite(parsed) ? parsed : undefined
}

const isValidDate = (date?: string) =>
  !!date && moment(date, 'YYYY-MM-DD', true).isValid()

const byDueDateAsc = (a: CycleBilling, b: CycleBilling) =>
  toTimestamp(a.dueDate) - toTimestamp(b.dueDate)

/**
 * Ordena billings por vencimento crescente sem mutar o array de origem.
 * Billings sem vencimento válido vão para o fim, preservando a ordem relativa.
 */
export function sortBillingsByDueDate(billings: CycleBilling[]) {
  const withDate = billings.filter((billing) => isValidDate(billing.dueDate))
  const withoutDate = billings.filter(
    (billing) => !isValidDate(billing.dueDate)
  )

  return [...withDate].sort(byDueDateAsc).concat(withoutDate)
}

/**
 * Localiza a billing de um ciclo específico da recorrência.
 * Quando há mais de uma no mesmo ciclo (reemissão), fica com a de vencimento
 * mais recente.
 */
export function findBillingByCycle(
  billings: CycleBilling[],
  cycle?: number
): CycleBilling | undefined {
  if (!billings?.length || !cycle || cycle < 1) return undefined

  const matches = billings.filter(
    (billing) => toCycleNumber(billing.recurrenceCycle) === cycle
  )
  if (!matches.length) return undefined

  return sortBillingsByDueDate(matches).slice(-1)[0]
}

/**
 * Fallback para quando o ciclo apontado por `current_cycle` não tem billing:
 * a primeira a vencer de hoje em diante e, se todas já venceram, a última.
 */
function findFallbackCurrent(billings: CycleBilling[]) {
  const ordered = sortBillingsByDueDate(
    billings.filter((billing) => isValidDate(billing.dueDate))
  )
  if (!ordered.length) return sortBillingsByDueDate(billings)[0]

  const today = moment().startOf('day')
  const upcoming = ordered.find(
    (billing) => !moment(billing.dueDate, 'YYYY-MM-DD').isBefore(today)
  )

  return upcoming || ordered[ordered.length - 1]
}

/**
 * Resolve o par (ciclo anterior, ciclo atual) exibido acima da tabela.
 *
 * A âncora é o `current_cycle` da recorrência e o anterior é `current_cycle - 1`.
 * `current_cycle` 0 (ou ausente) é tratado como 1 — recorrência que ainda não
 * rodou nenhum ciclo mostra o primeiro.
 *
 * Se o ciclo apontado não tiver billing emitida, cai para o próximo ciclo a
 * vencer (e, em último caso, para o mais recente já vencido). Nunca salta para
 * o fim da série, que exibiria um vencimento distante sem relação com o atual.
 */
export function resolveHighlightedCycles(
  billings: CycleBilling[],
  currentCycle?: number | string | null
): { previous?: CycleBilling; current?: CycleBilling } {
  if (!billings?.length) return {}

  // current_cycle 0/ausente => primeiro ciclo
  const anchor = toCycleNumber(currentCycle) || 1

  const current =
    findBillingByCycle(billings, anchor) || findFallbackCurrent(billings)

  if (!current) return {}

  // O anterior sai do ciclo da billing efetivamente exibida — assim o par
  // continua consistente mesmo quando o fallback acima entra em ação
  const currentNumber = toCycleNumber(current.recurrenceCycle)
  const previous =
    currentNumber && currentNumber > 1
      ? findBillingByCycle(billings, currentNumber - 1)
      : undefined

  return { previous, current }
}
